import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  useInventoryItems,
  useCreateInventoryItem,
  useInventoryCategories,
  useInventoryLocations,
  useCreateCategory,
  useCreateLocation,
} from '../../../platform/api/hooks';
import { asList, asRecord, str, getMeta } from '../../../platform/api/utils';
import { PageHeader } from '../../../components/ui/PageHeader';
import { Badge } from '../../../components/ui/Badge';
import { Section } from '../../../components/ui/Section';
import {
  type CreateForm,
  STATUS_LABELS,
  STATUS_VARIANTS,
  VISIBILITY_LABELS,
  createFormSchema,
  Th,
  Td,
  PagBtn,
  Skeleton,
} from './inventory-list.support';
import { InventoryCreateModal } from './inventory-list.modals';
import { onlyIf, usePermissions } from '../../../platform/auth/permissions';

let idCounter = 0;
export function InventoryList() {
  const { can } = usePermissions();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);
  const params: Record<string, unknown> = { page, limit: 20 };
  if (search) params.search = search;
  if (statusFilter) params.status = statusFilter;
  if (categoryFilter) params.categoryId = categoryFilter;
  const { data, isLoading, isError } = useInventoryItems(params);
  const createItem = useCreateInventoryItem();
  const createCategory = useCreateCategory();
  const createLocation = useCreateLocation();
  const { data: catData } = useInventoryCategories();
  const { data: locData } = useInventoryLocations();
  const categories = asList(catData);
  const locations = asList(locData);
  const items = asList(data);
  const meta = getMeta(data);
  const handleSearch = useCallback((value: string) => {
    setSearch(value);
    setPage(1);
  }, []);
  const form = useForm<CreateForm>({
    resolver: zodResolver(createFormSchema),
    defaultValues: {
      name: '',
      unit: 'un',
      currentQuantity: '0',
      minimumQuantity: '',
      categoryId: '',
      locationId: '',
      description: '',
    },
  });
  function handleCreate(data: CreateForm) {
    createItem.mutate({
      name: data.name,
      unit: data.unit,
      currentQuantity: data.currentQuantity ?? '0',
      minimumQuantity: data.minimumQuantity !== '' ? data.minimumQuantity : undefined,
      categoryId: data.categoryId !== '' ? data.categoryId : undefined,
      locationId: data.locationId !== '' ? data.locationId : undefined,
      description: data.description !== '' ? data.description : undefined,
    });
    setShowCreate(false);
    form.reset();
  }
  if (isError) {
    return (
      <div style={{ padding: '2.25rem 2.5rem', maxWidth: 1320 }}>
        <PageHeader title="Estoque" subtitle="Materiais e insumos da casa" />
        <Section title="Erro">
          <p style={{ color: 'var(--danger)', fontSize: '0.875rem', margin: 0 }}>
            Não foi possível carregar a lista de itens.
          </p>
        </Section>
      </div>
    );
  }
  const totalPages = Number(meta.totalPages) || 1;
  return (
    <div style={{ padding: '2.25rem 2.5rem', maxWidth: 1320 }}>
      <PageHeader
        title="Estoque"
        subtitle={`${str(meta.total)} itens no estoque`}
        action={onlyIf(can('inventory.items.create'), {
          label: '+ Novo item',
          onClick: () => {
            setShowCreate(true);
          },
        })}
      />
      {/* Filtros */}
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Buscar por nome..."
          value={search}
          onChange={(e) => {
            handleSearch(e.target.value);
          }}
          style={{
            flex: 1,
            maxWidth: 280,
            padding: '0.5rem 0.75rem',
            border: '1px solid var(--line)',
            borderRadius: 10,
            fontSize: '0.875rem',
            outline: 'none',
          }}
        />
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
          style={{
            padding: '0.5rem 0.75rem',
            border: '1px solid var(--line)',
            borderRadius: 10,
            fontSize: '0.875rem',
            outline: 'none',
            background: '#fff',
            minWidth: 150,
          }}
        >
          <option value="">Todos os status</option>
          {Object.entries(STATUS_LABELS).map(([v, l]) => (
            <option key={v} value={v}>
              {l}
            </option>
          ))}
        </select>
        <select
          value={categoryFilter}
          onChange={(e) => {
            setCategoryFilter(e.target.value);
            setPage(1);
          }}
          style={{
            padding: '0.5rem 0.75rem',
            border: '1px solid var(--line)',
            borderRadius: 10,
            fontSize: '0.875rem',
            outline: 'none',
            background: '#fff',
            minWidth: 150,
          }}
        >
          <option value="">Todas as categorias</option>
          {categories.map((c) => (
            <option key={str(c.id)} value={str(c.id)}>
              {str(c.name)}
            </option>
          ))}
        </select>
      </div>
      {/* Skeleton */}
      {isLoading && <Skeleton />}
      {/* Tabela */}
      {!isLoading && (
        <Section title="Itens">
          {items.length === 0 ? (
            <div
              style={{
                textAlign: 'center',
                padding: '3rem 1rem',
                color: 'var(--faint)',
                fontSize: '0.875rem',
              }}
            >
              {search || statusFilter || categoryFilter
                ? 'Nenhum item encontrado com esses filtros.'
                : 'Nenhum item cadastrado no estoque ainda.'}
            </div>
          ) : (
            <>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--line-soft)' }}>
                    <Th>Item</Th>
                    <Th>Categoria</Th>
                    <Th>Un.</Th>
                    <Th>Qtd</Th>
                    <Th>Mínimo</Th>
                    <Th>Situação</Th>
                    <Th>Vis.</Th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((raw) => {
                    const item = asRecord(raw);
                    const st = str(item.status);
                    const cat = item.category as Record<string, unknown> | undefined;
                    return (
                      <tr
                        key={str(item.id, String(idCounter++))}
                        onClick={() => {
                          void navigate(`/estoque/${String(item.id)}`);
                        }}
                        style={{
                          cursor: 'pointer',
                          borderBottom: '1px solid var(--soft)',
                          transition: 'background 0.15s',
                        }}
                      >
                        <Td>
                          <span style={{ fontWeight: 500, color: 'var(--green)' }}>
                            {str(item.name)}
                          </span>
                          {cat && (
                            <span
                              style={{
                                fontSize: '0.75rem',
                                color: 'var(--faint)',
                                marginLeft: '0.4rem',
                              }}
                            >
                              {str(cat.name)}
                            </span>
                          )}
                        </Td>
                        <Td style={{ fontSize: '0.85rem', color: 'var(--muted)' }}>
                          {cat ? str(cat.name) : '—'}
                        </Td>
                        <Td style={{ fontSize: '0.85rem', color: 'var(--muted)' }}>
                          {str(item.unit)}
                        </Td>
                        <Td style={{ fontWeight: 600 }}>{str(item.currentQuantity)}</Td>
                        <Td style={{ fontSize: '0.85rem', color: 'var(--muted)' }}>
                          {item.minimumQuantity ? str(item.minimumQuantity) : '—'}
                        </Td>
                        <Td>
                          <Badge
                            label={STATUS_LABELS[st] ?? st}
                            variant={STATUS_VARIANTS[st] ?? 'default'}
                          />
                        </Td>
                        <Td style={{ fontSize: '0.8rem', color: 'var(--muted)' }}>
                          {VISIBILITY_LABELS[str(item.visibility)] ?? str(item.visibility)}
                        </Td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {totalPages > 1 && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: '0.25rem',
                    marginTop: '1rem',
                  }}
                >
                  <PagBtn
                    disabled={page <= 1}
                    onClick={() => {
                      setPage(page - 1);
                    }}
                    label="<"
                  />
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                    <PagBtn
                      key={p}
                      active={p === page}
                      onClick={() => {
                        setPage(p);
                      }}
                      label={String(p)}
                    />
                  ))}
                  <PagBtn
                    disabled={page >= totalPages}
                    onClick={() => {
                      setPage(page + 1);
                    }}
                    label=">"
                  />
                </div>
              )}
            </>
          )}
        </Section>
      )}
      {showCreate && (
        <InventoryCreateModal
          form={form}
          categories={categories}
          locations={locations}
          createCategory={createCategory}
          createLocation={createLocation}
          createPending={createItem.isPending}
          onCancel={() => {
            setShowCreate(false);
            form.reset();
          }}
          onCreate={() => {
            void form.handleSubmit(handleCreate)();
          }}
        />
      )}
    </div>
  );
}
